<script setup>
/**
 * FormControlEmailMessageHtmlBody
 * Provides a tabbed interface for Email HTML:
 * 1. Preview: An iframe rendering the HTML content.
 * 2. HTML: A code editor for direct manipulation.
 */
import {ref, computed, onMounted, nextTick} from 'vue'
import FormControlTypeCodeEditor from '@/common/form-control-type/FormControlTypeCodeEditor.vue'

const props = defineProps({
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    },
    value: {
        type: String,
        default: ''
    }
})

const activeTab = ref('preview')
const iframeRef = ref(null)

const iframeHtml = computed(() => props.currentData[props.field.name] || '')

const updateIframeHeight = () => {
    const iframe = iframeRef.value
    if (iframe && iframe.contentWindow) {
        iframe.style.height = '250px'
        const newHeight = iframe.contentWindow.document.body.offsetHeight
        iframe.style.height = (newHeight + 20) + 'px'
    }
}

const setTab = async (tab) => {
    activeTab.value = tab
    if (tab === 'preview') {
        await nextTick()
    }
}

onMounted(() => {
    props.field.required = false
})
</script>

<template>
    <div>
        <ul class="nav nav-tabs" role="tablist">
            <li class="nav-item" role="presentation">
                <a
                    class="nav-link"
                    :class="{ active: activeTab === 'preview' }"
                    href="#"
                    @click.prevent="setTab('preview')"
                >
                    Preview
                </a>
            </li>
            <li class="nav-item" role="presentation">
                <a
                    class="nav-link"
                    :class="{ active: activeTab === 'html' }"
                    href="#"
                    @click.prevent="setTab('html')"
                >
                    HTML
                </a>
            </li>
        </ul>

        <div class="tab-content">
            <div
                v-if="activeTab === 'preview'"
                class="tab-pane active"
                role="tabpanel"
            >
                <iframe
                    ref="iframeRef"
                    :srcdoc="iframeHtml"
                    @load="updateIframeHeight"
                    style="width: 100%; min-height: 250px; border: solid #ddd; border-width: 0 1px 1px 1px"
                />
            </div>

            <div
                v-if="activeTab === 'html'"
                class="tab-pane active"
                role="tabpanel"
            >
                <FormControlTypeCodeEditor v-bind="$props"/>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Standard Bootstrap tab-pane behavior usually requires 'active' class to show */
.tab-pane {
    display: none;
}

.tab-pane.active {
    display: block;
}
</style>